/**
 * Cookie Consent Banner — GDPR/PKE compliant
 *
 * Features:
 * - Granular category controls (necessary, statistics, marketing)
 * - Google Consent Mode v2 integration
 * - GPC (Global Privacy Control) signal respect
 * - Migration from old "accepted" cookie format
 * - Tabbed dialog UI
 *
 * @package VSC
 */
/* eslint-disable */
// Ported verbatim from the programylojalnosciowe.pl VSC theme banner so both
// sites share an identical cookie banner. Only GTM/GA4 IDs differ. Runs once,
// invoked from the CookieConsent React component after the markup mounts.
export function initCookieConsent() {
	'use strict';

	if (typeof window === 'undefined' || window.__vscCookieConsentInit) {
		return;
	}
	window.__vscCookieConsentInit = true;

	var COOKIE_NAME = 'vsc_cookie_consent';
	var COOKIE_DAYS = 365;

	// Load tags only on the production host, so test/staging/localhost traffic
	// never reaches the analytics reports.
	var host = window.location.hostname;
	var IS_PRODUCTION = host === 'secretclient.com' || host === 'www.secretclient.com';
	var GTM_ID = IS_PRODUCTION ? 'GTM-WRC7HVQQ' : '';
	var GA4_ID = IS_PRODUCTION ? 'G-8GSTYHMCH8' : ''; // WeNet
	var GA4_ID_OWN = IS_PRODUCTION ? 'G-JX89DYS7ZS' : ''; // VSC (nasze)

	// --- Google Consent Mode v2: push default deny BEFORE GTM loads ---
	window.dataLayer = window.dataLayer || [];
	function gtag() { window.dataLayer.push(arguments); }
	gtag('consent', 'default', {
		analytics_storage: 'denied',
		ad_storage: 'denied',
		ad_user_data: 'denied',
		ad_personalization: 'denied',
		functionality_storage: 'granted',
		security_storage: 'granted',
		wait_for_update: 500
	});

	// While ad consent is denied, redact ad-click identifiers (gclid) from requests to Google.
	gtag('set', 'ads_data_redaction', true);

	// --- Cookie helpers ---

	function getCookie(name) {
		var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
		return match ? decodeURIComponent(match[2]) : null;
	}

	function setCookie(name, value, days) {
		var date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		document.cookie = name + '=' + encodeURIComponent(value) +
			';expires=' + date.toUTCString() +
			';path=/;SameSite=Lax' + (location.protocol === 'https:' ? ';Secure' : '');
	}

	// Delete a cookie across every likely domain (host, .host and the registrable domain),
	// because gtag stores _ga on the registrable domain while the banner uses the host.
	function deleteCookie(name) {
		var host = window.location.hostname;
		var parts = host.split('.');
		var domains = ['', host, '.' + host];
		if (parts.length > 2) {
			var root = parts.slice(-2).join('.');
			domains.push('.' + root, root);
		}
		var expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
		for (var i = 0; i < domains.length; i++) {
			document.cookie = name + '=;expires=' + expired + ';path=/' +
				(domains[i] ? ';domain=' + domains[i] : '');
		}
	}

	// Remove Google cookies whose consent was withdrawn (analytics: _ga/_gid, ads: _gcl_).
	function clearDeniedTrackingCookies(consent) {
		var prefixes = [];
		if (!consent.s) { prefixes.push('_ga', '_gid'); }
		if (!consent.m) { prefixes.push('_gcl_'); }
		if (!prefixes.length) { return; }

		var names = document.cookie.split(';').map(function (c) { return c.split('=')[0].trim(); });
		names.forEach(function (name) {
			for (var i = 0; i < prefixes.length; i++) {
				if (name.indexOf(prefixes[i]) === 0) {
					deleteCookie(name);
					return;
				}
			}
		});
	}

	// --- Consent state ---

	function defaultConsent() {
		return { n: true, s: false, m: false, t: null };
	}

	function getConsent() {
		var raw = getCookie(COOKIE_NAME);
		if (!raw) {
			return null;
		}

		// Migrate old format
		if (raw === 'accepted') {
			var migrated = { n: true, s: true, m: true, t: new Date().toISOString() };
			saveConsent(migrated);
			return migrated;
		}

		try {
			return JSON.parse(raw);
		} catch (e) {
			return null;
		}
	}

	function saveConsent(consent) {
		var toSave = {
			n: true,
			s: Boolean(consent.s),
			m: Boolean(consent.m),
			t: new Date().toISOString()
		};
		setCookie(COOKIE_NAME, JSON.stringify(toSave), COOKIE_DAYS);
		return toSave;
	}

	// --- Tracking loaders ---

	function loadGTM() {
		if (!GTM_ID) {
			return;
		}
		if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
			return;
		}
		var w = window, d = document, l = 'dataLayer';
		w[l] = w[l] || [];
		w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
		var f = d.getElementsByTagName('script')[0];
		var j = d.createElement('script');
		j.async = true;
		j.src = 'https://www.googletagmanager.com/gtm.js?id=' + GTM_ID;
		f.parentNode.insertBefore(j, f);
	}

	function loadGA4() {
		if (!GA4_ID && !GA4_ID_OWN) {
			return;
		}
		if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
			return;
		}
		var script = document.createElement('script');
		script.async = true;
		script.src = 'https://www.googletagmanager.com/gtag/js?id=' + (GA4_ID || GA4_ID_OWN);
		document.head.appendChild(script);

		gtag('js', new Date());
		if (GA4_ID) {
			gtag('config', GA4_ID); // WeNet
		}
		if (GA4_ID_OWN) {
			gtag('config', GA4_ID_OWN); // VSC (nasze)
		}
	}

	// Advanced Consent Mode: the tags always load, regardless of consent.
	// gtag.js decides how much data it may send based on the consent signals
	// set above; without consent it sends only a cookieless ping (gcs=G100).
	// Do not gate this on consent: blocking the load = Basic Consent Mode.
	loadGTM();
	loadGA4();

	function applyConsent(consent) {
		// Push Consent Mode v2 update
		gtag('consent', 'update', {
			analytics_storage: consent.s ? 'granted' : 'denied',
			ad_storage: consent.m ? 'granted' : 'denied',
			ad_user_data: consent.m ? 'granted' : 'denied',
			ad_personalization: consent.m ? 'granted' : 'denied'
		});

		// After consent is withdrawn, remove cookies gtag will not clear on its own.
		clearDeniedTrackingCookies(consent);
	}

	// --- UI helpers ---

	function getBanner() {
		return document.getElementById('vsc-cookie-consent');
	}

	function getSettingsBtn() {
		return document.getElementById('vsc-cookie-settings-btn');
	}

	function showBanner(tabId) {
		var banner = getBanner();
		if (!banner) {
			return;
		}
		banner.setAttribute('aria-hidden', 'false');
		banner.classList.add('vsc-cookie-consent--visible');
		document.body.style.overflow = 'hidden';

		if (tabId) {
			switchTab(tabId);
		}

		// Restore toggles from saved consent
		var consent = getConsent();
		if (consent) {
			setToggles(consent);
		} else {
			setToggles(defaultConsent());
		}

		var settingsBtn = getSettingsBtn();
		if (settingsBtn) {
			settingsBtn.classList.remove('vsc-cookie-settings-btn--visible');
		}
	}

	function hideBanner() {
		var banner = getBanner();
		if (!banner) {
			return;
		}
		banner.setAttribute('aria-hidden', 'true');
		banner.classList.remove('vsc-cookie-consent--visible');
		document.body.style.overflow = '';

		var settingsBtn = getSettingsBtn();
		if (settingsBtn) {
			settingsBtn.classList.add('vsc-cookie-settings-btn--visible');
		}
	}

	function switchTab(tabId) {
		var tabs = document.querySelectorAll('.vsc-cookie-consent__tab');
		var panels = document.querySelectorAll('.vsc-cookie-consent__panel');

		for (var i = 0; i < tabs.length; i++) {
			var isActive = tabs[i].getAttribute('data-tab') === tabId;
			tabs[i].classList.toggle('vsc-cookie-consent__tab--active', isActive);
			tabs[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
		}

		for (var j = 0; j < panels.length; j++) {
			var isVisible = panels[j].id === 'vsc-tab-' + tabId;
			panels[j].classList.toggle('vsc-cookie-consent__panel--active', isVisible);
			panels[j].setAttribute('aria-hidden', isVisible ? 'false' : 'true');
		}
	}

	function getTogglesState() {
		var statsToggle = document.getElementById('vsc-toggle-statistics');
		var marketingToggle = document.getElementById('vsc-toggle-marketing');
		return {
			n: true,
			s: statsToggle ? statsToggle.checked : false,
			m: marketingToggle ? marketingToggle.checked : false
		};
	}

	function setToggles(consent) {
		var statsToggle = document.getElementById('vsc-toggle-statistics');
		var marketingToggle = document.getElementById('vsc-toggle-marketing');
		if (statsToggle) {
			statsToggle.checked = Boolean(consent.s);
		}
		if (marketingToggle) {
			marketingToggle.checked = Boolean(consent.m);
		}
	}

	// --- GPC detection ---

	function isGPCEnabled() {
		return navigator.globalPrivacyControl === true;
	}

	// --- Event handlers ---

	function handleAcceptAll() {
		var consent = saveConsent({ n: true, s: true, m: true });
		applyConsent(consent);
		hideBanner();
	}

	function handleReject() {
		var consent = saveConsent({ n: true, s: false, m: false });
		applyConsent(consent);
		hideBanner();
	}

	function handleConfirmSelected() {
		var toggles = getTogglesState();
		var consent = saveConsent(toggles);
		applyConsent(consent);
		hideBanner();
	}

	function handleSettingsOpen() {
		showBanner('details');
	}

	// --- Initialization ---

	function bindEvents() {
		var acceptAllBtns = document.querySelectorAll('.vsc-cookie-btn--accept-all');
		var rejectBtns = document.querySelectorAll('.vsc-cookie-btn--reject');
		var confirmBtn = document.getElementById('vsc-cookie-confirm-selected');
		var settingsBtn = getSettingsBtn();
		var tabs = document.querySelectorAll('.vsc-cookie-consent__tab');

		for (var i = 0; i < acceptAllBtns.length; i++) {
			acceptAllBtns[i].addEventListener('click', handleAcceptAll);
		}
		for (var j = 0; j < rejectBtns.length; j++) {
			rejectBtns[j].addEventListener('click', handleReject);
		}
		if (confirmBtn) {
			confirmBtn.addEventListener('click', handleConfirmSelected);
		}
		if (settingsBtn) {
			settingsBtn.addEventListener('click', handleSettingsOpen);
		}

		for (var k = 0; k < tabs.length; k++) {
			tabs[k].addEventListener('click', function () {
				switchTab(this.getAttribute('data-tab'));
			});
		}
	}

	function showGPCNotice() {
		var notice = document.getElementById('vsc-gpc-notice');
		if (notice) {
			notice.style.display = '';
		}
	}

	function init() {
		var consent = getConsent();
		var gpcEnabled = isGPCEnabled();

		if (gpcEnabled) {
			showGPCNotice();
		}

		// GPC: auto-reject non-essential, show only settings button
		if (gpcEnabled && !consent) {
			var gpcConsent = saveConsent({ n: true, s: false, m: false });
			applyConsent(gpcConsent);
			var settingsBtn = getSettingsBtn();
			if (settingsBtn) {
				settingsBtn.classList.add('vsc-cookie-settings-btn--visible');
			}
			bindEvents();
			return;
		}

		// Existing consent — apply it, show settings button
		if (consent) {
			applyConsent(consent);
			var btn = getSettingsBtn();
			if (btn) {
				btn.classList.add('vsc-cookie-settings-btn--visible');
			}
			bindEvents();
			return;
		}

		// No consent yet — show banner
		bindEvents();
		showBanner('consent');
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
}
